import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';

export type UseTaskFieldOptions<T> = {
  /**
   * What `value` belongs to — the task id. When it changes the row hard-resets (draft, edit mode,
   * dirty) even mid-edit, and a save already in flight stops writing back here, so a draft can
   * never be committed onto a different task than the one it was typed for.
   */
  identity?: string;
  /** The value as the store currently holds it. The draft follows it while the row is not edited. */
  value: T;
  save: (next: T) => Promise<void>;
  successMessage?: string;
  errorMessage?: string;
  /** Compares the draft with `value` for `dirty`/resync. Defaults to `Object.is`. */
  equals?: (a: T, b: T) => boolean;
  /**
   * Errors the store has already reported to the user (e.g. `isInlineImagesNotAllowedError`).
   * `commit` skips its own `errorMessage` for those instead of showing two toasts.
   */
  isSilentError?: (error: unknown) => boolean;
};

export type TaskField<T> = {
  draft: T;
  setDraft: (next: T) => void;
  editing: boolean;
  startEdit: () => void;
  /** Drops the draft and leaves edit mode. */
  cancel: () => void;
  /**
   * Saves `next` (or the current draft) through `save`. Never throws: it reports the failure and
   * leaves `editing` on so the user can retry or copy their text out. Resolves to `true` only when
   * the value reached the server — callers that own their own edit state (the title row) use that
   * to decide whether to stay open.
   */
  commit: (next?: T) => Promise<boolean>;
  saving: boolean;
  dirty: boolean;
};

const defaultEquals = <T,>(a: T, b: T): boolean => Object.is(a, b);

/**
 * One editable property of a task: a draft, an edit flag and a guarded save.
 *
 * Used by every row of the task drawer so they all behave the same — optimistic-free, one toast per
 * save, and no silent loss of what the user typed when the request fails.
 */
export function useTaskField<T>(options: UseTaskFieldOptions<T>): TaskField<T> {
  const { value } = options;

  const [draft, setDraftState] = useState<T>(value);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Everything the callbacks read lives in a ref so they can stay stable across renders.
  const optionsRef = useRef(options);
  const draftRef = useRef(draft);
  const editingRef = useRef(editing);
  const savingRef = useRef(saving);
  const identityRef = useRef(options.identity);

  useEffect(() => {
    optionsRef.current = options;
  });

  // A different task is being shown: drop everything that belonged to the previous one, right here
  // in the render pass, so no child ever sees the old draft against the new task. An effect would
  // be too late — a click handler could fire first and save A's text onto B.
  if (identityRef.current !== options.identity) {
    identityRef.current = options.identity;
    draftRef.current = value;
    editingRef.current = false;
    savingRef.current = false;
    setDraftState(value);
    setEditing(false);
    setSaving(false);
  }

  const equals = options.equals ?? defaultEquals;

  const applyDraft = useCallback((next: T) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  const setEditingFlag = useCallback((next: boolean) => {
    editingRef.current = next;
    setEditing(next);
  }, []);

  // Follow the store while the row is idle; never overwrite what the user is typing.
  useEffect(() => {
    if (editingRef.current || savingRef.current) {
      return;
    }
    const same = optionsRef.current.equals ?? defaultEquals;
    if (same(draftRef.current, value)) {
      return;
    }
    applyDraft(value);
  }, [value, applyDraft]);

  const startEdit = useCallback(() => {
    applyDraft(optionsRef.current.value);
    setEditingFlag(true);
  }, [applyDraft, setEditingFlag]);

  const cancel = useCallback(() => {
    applyDraft(optionsRef.current.value);
    setEditingFlag(false);
  }, [applyDraft, setEditingFlag]);

  const commit = useCallback(
    async (next?: T): Promise<boolean> => {
      if (savingRef.current) {
        return false;
      }
      const { save, successMessage, errorMessage, isSilentError } = optionsRef.current;
      const payload = next !== undefined ? next : draftRef.current;
      // Which task this save belongs to. If the drawer moves on while the request is in flight,
      // nothing below may touch the row any more.
      const savedFor = identityRef.current;

      // The draft becomes the payload up front so a failed save still leaves the value on screen.
      applyDraft(payload);
      savingRef.current = true;
      setSaving(true);
      try {
        await save(payload);
        if (identityRef.current !== savedFor) {
          return true;
        }
        setEditingFlag(false);
        if (successMessage) {
          message.success(successMessage);
        }
        return true;
      } catch (error) {
        if (identityRef.current !== savedFor) {
          return false;
        }
        // While editing, the draft is the only copy of what the user wrote — keep it and stay open.
        // A control that saves without an edit mode (the header's status select) must instead snap
        // back, or it would keep showing a value the server rejected.
        if (!editingRef.current) {
          applyDraft(optionsRef.current.value);
        }
        if (errorMessage && !isSilentError?.(error)) {
          message.error(errorMessage);
        }
        return false;
      } finally {
        // The identity reset already cleared these for the new task; don't undo its work.
        if (identityRef.current === savedFor) {
          savingRef.current = false;
          setSaving(false);
        }
      }
    },
    [applyDraft, setEditingFlag],
  );

  return {
    draft,
    setDraft: applyDraft,
    editing,
    startEdit,
    cancel,
    commit,
    saving,
    dirty: !equals(draft, value),
  };
}

export default useTaskField;
